/**
 * Upload tab — publish one or many `.tar.gz` sdists to the private
 * PyPI index.
 *
 * Files dropped or picked are added to a per-row queue. Each row goes
 * through its own state machine — pending → hashing → uploading →
 * (done | failed) — and the queue is processed sequentially so the
 * UI surfaces stable, ordered progress and partial failures cleanly.
 *
 * Failed rows can be selected (individually or via "Select all failed")
 * and re-submitted with `force=true`, which tells the server to first
 * delete the existing release from pypiserver + catalog and then
 * publish the new artifact in its place.
 *
 * The SHA-256 is computed in the browser via `crypto.subtle.digest`
 * so the server can detect mid-flight corruption or substitution.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef, useState, type CSSProperties, type ChangeEvent, type DragEvent } from 'react';

import { ApiError } from '@optum/api-client/client';
import { publishSdist } from '@optum/api-client/endpoints/catalog';
import type { CatalogPublishResponse } from '@optum/api-client/types';

import { Button } from '../../ui/Button.js';
import { DividedList } from '../../ui/DividedList.js';
import { SectionCard, SectionCardBody } from '../../ui/SectionCard.js';
import { useToast } from '../../ui/useToast.js';
import { Checkbox } from '../../ui/Checkbox.js';
import { useConfirm } from '../../ui/Confirm.js';

type RowStatus =
  | { kind: 'pending' }
  | { kind: 'hashing' }
  | { kind: 'uploading' }
  | { kind: 'done'; result: CatalogPublishResponse }
  | { kind: 'failed'; error: string };

interface QueueRow {
  id: string;
  file: File;
  status: RowStatus;
  /** Only meaningful while `status.kind === 'failed'`. */
  selected: boolean;
}

async function sha256Hex(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buf);
  const bytes = new Uint8Array(digest);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

const ACCEPTED = '.tar.gz,application/gzip,application/x-gzip';

export function UploadTab() {
  const qc = useQueryClient();
  const toast = useToast();
  const [rows, setRows] = useState<QueueRow[]>([]);
  const [hovering, setHovering] = useState(false);
  const confirm = useConfirm();
  const fileInputRef = useRef<HTMLInputElement>(null);

  function updateRow(id: string, patch: Partial<QueueRow>) {
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  }

  function setRowStatus(id: string, status: RowStatus) {
    setRows((rs) =>
      rs.map((r) =>
        r.id === id
          ? {
              ...r,
              status,
              // Clear selection whenever the row leaves the `failed` state.
              selected: status.kind === 'failed' ? r.selected : false,
            }
          : r,
      ),
    );
  }

  function addFiles(list: FileList | File[]) {
    const incoming = Array.from(list);
    const accepted: QueueRow[] = [];
    const rejected: string[] = [];
    let counter = Date.now();
    for (const file of incoming) {
      if (!file.name.endsWith('.tar.gz')) {
        rejected.push(file.name);
        continue;
      }
      accepted.push({
        id: `${counter++}-${file.name}`,
        file,
        status: { kind: 'pending' },
        selected: false,
      });
    }
    if (rejected.length) {
      toast.error('Skipped non-sdist files', rejected.join(', '));
    }
    if (accepted.length) {
      setRows((rs) => [...rs, ...accepted]);
    }
  }

  function removeRow(id: string) {
    setRows((rs) => rs.filter((r) => r.id !== id));
  }

  function clearAll() {
    setRows([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  async function publishRow(row: QueueRow, force = false): Promise<RowStatus> {
    try {
      setRowStatus(row.id, { kind: 'hashing' });
      const sha = await sha256Hex(row.file);
      setRowStatus(row.id, { kind: 'uploading' });
      const result = await publishSdist({ sdist: row.file, sha256: sha, force });
      return { kind: 'done', result };
    } catch (err) {
      const message = err instanceof ApiError ? err.message : String(err);
      return { kind: 'failed', error: message };
    }
  }

  const publishMutation = useMutation({
    mutationFn: async () => {
      const toProcess = rows.filter(
        (r) => r.status.kind === 'pending' || r.status.kind === 'failed',
      );
      let done = 0;
      let failed = 0;
      for (const row of toProcess) {
        const final = await publishRow(row);
        setRowStatus(row.id, final);
        if (final.kind === 'done') done++;
        else failed++;
      }
      return { done, failed };
    },
    onSuccess: ({ done, failed }) => {
      qc.invalidateQueries({ queryKey: ['catalog'] });
      if (done > 0) {
        toast.success(`Published ${done} sdist${done === 1 ? '' : 's'}`);
      }
      if (failed > 0) {
        toast.error(
          `${failed} failed`,
          'Inspect each row for details; select and Force replace to overwrite.',
        );
      }
    },
  });

  const forceReplaceMutation = useMutation({
    mutationFn: async () => {
      const targets = rows.filter((r) => r.status.kind === 'failed' && r.selected);
      let done = 0;
      let failed = 0;
      for (const row of targets) {
        const final = await publishRow(row, true);
        setRowStatus(row.id, final);
        if (final.kind === 'done') done++;
        else failed++;
      }
      return { done, failed };
    },
    onSuccess: ({ done, failed }) => {
      qc.invalidateQueries({ queryKey: ['catalog'] });
      if (done > 0) {
        toast.success(`Force-replaced ${done} sdist${done === 1 ? '' : 's'}`);
      }
      if (failed > 0) {
        toast.error(
          `${failed} still failing`,
          'The server rejected the replacement — inspect each row.',
        );
      }
    },
  });

  function handlePick(ev: ChangeEvent<HTMLInputElement>) {
    if (ev.target.files && ev.target.files.length) {
      addFiles(ev.target.files);
    }
    ev.target.value = '';
  }

  function handleDrop(ev: DragEvent<HTMLDivElement>) {
    ev.preventDefault();
    setHovering(false);
    if (ev.dataTransfer.files && ev.dataTransfer.files.length) {
      addFiles(ev.dataTransfer.files);
    }
  }

  const pendingCount = rows.filter(
    (r) => r.status.kind === 'pending' || r.status.kind === 'failed',
  ).length;
  const failedRows = rows.filter((r) => r.status.kind === 'failed');
  const failedCount = failedRows.length;
  const selectedFailedCount = failedRows.filter((r) => r.selected).length;
  const allFailedSelected = failedCount > 0 && selectedFailedCount === failedCount;
  const inFlight = publishMutation.isPending || forceReplaceMutation.isPending;

  function toggleAllFailed() {
    const next = !allFailedSelected;
    setRows((rs) => rs.map((r) => (r.status.kind === 'failed' ? { ...r, selected: next } : r)));
  }

  return (
    <SectionCard>
      <SectionCardBody>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setHovering(true);
            }}
            onDragLeave={() => setHovering(false)}
            onDrop={handleDrop}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 7,
              borderRadius: 'var(--radius-control)',
              border: `2px dashed ${hovering ? 'var(--accent)' : 'var(--border-input)'}`,
              background: hovering ? 'var(--accent-tint)' : 'var(--surface-sunken)',
              padding: 30,
              textAlign: 'center',
            }}
          >
            <p style={{ fontSize: 'var(--text-body-md)', color: 'var(--text-muted)' }}>
              Drag &amp; drop one or more <code className="mono">.tar.gz</code> files here
            </p>
            <p style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--text-muted)' }}>— or —</p>
            <label
              style={{
                fontSize: 'var(--text-meta-lg)',
                fontWeight: 'var(--weight-medium)',
                color: 'var(--text-accent)',
                textDecoration: 'underline',
              }}
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={ACCEPTED}
                onChange={handlePick}
                className="sr-only"
              />
              Choose files
            </label>
          </div>

          <p style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--text-muted)' }}>
            The browser hashes each file with SHA-256 before upload; the server verifies the hash,
            inspects <code className="mono">PKG-INFO</code> and{' '}
            <code className="mono">pyproject.toml</code>, pushes the artifact to the private index,
            and records the catalog row.
          </p>

          {failedCount > 0 && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 11,
                borderRadius: 'var(--radius-control)',
                border: '1px solid var(--border-hairline)',
                background: 'var(--surface-sunken)',
                padding: 11,
              }}
            >
              <Checkbox
                checked={allFailedSelected}
                onChange={toggleAllFailed}
                disabled={inFlight}
                label={`Select all failed (${failedCount})`}
              />
              <div style={{ flex: '1 1 0%' }} />
              <Button
                type="button"
                variant="danger"
                disabled={selectedFailedCount === 0 || inFlight}
                onClick={() => {
                  void confirm({
                    title: `Replace ${selectedFailedCount} release${selectedFailedCount === 1 ? '' : 's'}?`,
                    description:
                      'The old archives will be deleted from the private index and overwritten with the selected uploads. This cannot be undone.',
                    confirmLabel: 'Force replace',
                    tone: 'danger',
                  }).then((ok) => {
                    if (ok) forceReplaceMutation.mutate();
                  });
                }}
              >
                Force replace ({selectedFailedCount})
              </Button>
            </div>
          )}

          {rows.length > 0 && (
            <DividedList
              style={{
                borderRadius: 'var(--radius-control)',
                border: '1px solid var(--border-hairline)',
              }}
            >
              {rows.map((row) => (
                <QueueRowView
                  key={row.id}
                  row={row}
                  disabled={inFlight}
                  onRemove={() => removeRow(row.id)}
                  onSelectChange={(selected) => updateRow(row.id, { selected })}
                />
              ))}
            </DividedList>
          )}

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 7,
            }}
          >
            <span style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--text-muted)' }}>
              {rows.length > 0
                ? `${rows.length} queued · ${pendingCount} pending`
                : 'Queue is empty.'}
            </span>
            <div style={{ display: 'flex', gap: 7 }}>
              <Button
                type="button"
                variant="ghost"
                onClick={clearAll}
                disabled={rows.length === 0 || inFlight}
              >
                Clear
              </Button>
              <Button
                variant="primary"
                type="button"
                onClick={() => publishMutation.mutate()}
                disabled={pendingCount === 0 || inFlight}
              >
                {publishMutation.isPending
                  ? 'Publishing…'
                  : `Publish ${pendingCount > 0 ? pendingCount : ''}`.trim()}
              </Button>
            </div>
          </div>
        </div>
      </SectionCardBody>
    </SectionCard>
  );
}

function QueueRowView({
  row,
  disabled,
  onRemove,
  onSelectChange,
}: {
  row: QueueRow;
  disabled: boolean;
  onRemove: () => void;
  onSelectChange: (selected: boolean) => void;
}) {
  const isFailed = row.status.kind === 'failed';
  return (
    <li style={{ display: 'flex', alignItems: 'center', gap: 11, padding: 11 }}>
      {isFailed ? (
        <Checkbox
          checked={row.selected}
          onChange={(checked) => onSelectChange(checked)}
          disabled={disabled}
          aria-label={`Select ${row.file.name} for force replace`}
        />
      ) : (
        // Placeholder keeps row alignment stable across mixed states.
        <span aria-hidden="true" style={{ height: 16, width: 16 }} />
      )}
      <div style={{ minWidth: 0, flex: '1 1 0%' }}>
        <p
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontSize: 'var(--text-meta-lg)',
            fontWeight: 'var(--weight-medium)',
          }}
        >
          {row.file.name}
        </p>
        <p style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--text-muted)' }}>
          {Math.round(row.file.size / 1024)} KB
          {row.status.kind === 'done' && (
            <>
              {' · '}
              published as {row.status.result.name} {row.status.result.version}
            </>
          )}
        </p>
        {row.status.kind === 'failed' && (
          <p
            title={row.status.error}
            style={{
              marginTop: 5,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: 'var(--text-meta-lg)',
              color: 'var(--danger)',
            }}
          >
            {row.status.error}
          </p>
        )}
      </div>
      <RowBadge status={row.status} />
      <button
        type="button"
        onClick={onRemove}
        disabled={disabled || row.status.kind === 'hashing' || row.status.kind === 'uploading'}
        aria-label={`Remove ${row.file.name}`}
        style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--text-muted)' }}
      >
        ×
      </button>
    </li>
  );
}

function RowBadge({ status }: { status: RowStatus }) {
  const map: Record<RowStatus['kind'], { label: string; tint: CSSProperties }> = {
    pending: {
      label: 'Queued',
      tint: { background: 'var(--surface-fill)', color: 'var(--text-muted)' },
    },
    hashing: { label: 'Hashing…', tint: { background: 'var(--info-tint)', color: 'var(--info)' } },
    uploading: {
      label: 'Uploading…',
      tint: { background: 'var(--info-tint)', color: 'var(--info)' },
    },
    done: {
      label: 'Published',
      tint: { background: 'var(--accent-tint)', color: 'var(--text-accent)' },
    },
    failed: { label: 'Failed', tint: { background: 'var(--danger-tint)', color: 'var(--danger)' } },
  };
  const entry = map[status.kind];
  return (
    <span
      style={{
        flexShrink: 0,
        borderRadius: 'var(--radius-pill)',
        padding: '5px 7px',
        fontSize: 'var(--text-meta-lg)',
        fontWeight: 'var(--weight-medium)',
        ...entry.tint,
      }}
    >
      {entry.label}
    </span>
  );
}
